import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import { QuestionnaireReviewer, QuizInviteType, TeamMemberRoleEnum, TeamMemberType } from '../../../lib/types'
import useInfiniteScroll from '../../../components/hooks/useInfiniteScrool'
import { usePageableRequest } from '../../../components/hooks/usePageableRequest'
import QuizItem from '../../../components/QuizItem'
import Typography from '@mui/material/Typography'
import api from '../../../lib/api'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import { CreateQuestionnaireContext } from '../../../components/CreateQuestionnaire'
import { SimpleDialogContext } from '../../../components/SimpleDialog'
import { SimpleSnackbarContext } from '../../../components/SimpleSnackbar'
import axios from 'axios'
import Divider from '@mui/material/Divider'
import useShareInvite from '../../../components/hooks/useShareInvite'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { SimpleMultiSelectDialogContext } from '../../../components/SimpleMultiSelectDialog'
import { UserContext } from '../../../components/UserProvider'
import CheckIcon from '@mui/icons-material/Check'
import DoDisturbIcon from '@mui/icons-material/DoDisturb'
import MenuItem from '@mui/material/MenuItem'
import { FormDialogContext } from '../../../components/FormDialog'
import { capitalize } from '../../../components/Capitalize'
import { usePermissions } from '../../../components/hooks/usePermissions'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import InviteReportModal from './InviteReportModal'

const QuestionnaireComponent: React.FC = () => {
  const handleShare = useShareInvite()
  const { actions: handleInviteesStats } = useContext(CreateQuestionnaireContext)
  const { actions: createQuestionnaireDialogActions } = useContext(CreateQuestionnaireContext)
  const { actions: simpleDialogActions } = useContext(SimpleDialogContext)
  const { actions: simpleMultiSelectDialogActions } = useContext(SimpleMultiSelectDialogContext)
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const { actions: formDialogActions } = useContext(FormDialogContext)
  const history = useHistory()
  const [searchValue, setSearchValue] = useState<string>('')
  const { user, member } = useContext(UserContext)
  const [teamMembers, setTeamMembers] = useState<TeamMemberType[]>([])
  const [reportQuestionnaire, setReportQuestionnaire] = useState<{ id: string; title: string } | null>(null)
  const { isCreator } = usePermissions()

  const filterAndTransformMembersByQuestionnaire = useCallback(
    (questionnaireReviewers: any[]) => {
      return teamMembers
        .filter(teamMember => teamMember.role !== TeamMemberRoleEnum.VIEWER && teamMember.id !== member?.id)
        .map(teamMember => ({
          id: teamMember.id,
          name: teamMember.name,
          email: teamMember.email,
          alreadyInvited: questionnaireReviewers.map(r => r.reviewerId).includes(teamMember.id)
        }))
    },
    [teamMembers, member]
  )

  const { loading, items, hasNextPage, error, loadMore, setItems, reload } = usePageableRequest<QuizInviteType>({
    size: 15,
    q: searchValue,
    request: api.getQuestionnaries
  })

  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const timer = setTimeout(() => reload(), 300)
    return () => clearTimeout(timer)
  }, [searchValue, reload])

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: !!error,
    rootMargin: '0px 0px 400px 0px'
  })

  const handleDelete = useCallback(
    async (id: string) => {
      simpleDialogActions.open(
        'Delete questionnaire',
        'Are you sure do you want delete this questionnaire?',
        { okText: 'Delete', cancelText: 'Cancel', showOk: true },
        async success => {
          if (success) {
            try {
              setItems(prev => prev.filter(item => item.id !== id))
              snackActions.open(
                'Sorry, the Rakonto team is currently working on the Questionnaire delete feature. For security reasons, it is temporarily unavailable.'
              )
            } catch (error) {
              if (axios.isAxiosError(error)) {
                // @ts-ignore
                const { code, message } = error.response.data
                if (code) {
                  snackActions.open(message)
                  return
                }
              }
              snackActions.open('Internal server error.')
            }
          }
        }
      )
    },
    [simpleDialogActions, snackActions, setItems]
  )

  const handleDownloadStats = useCallback(async (questionnaire: { id: string }) => {
    await api.downloadQuestionnaireStats(questionnaire.id)
  }, [])

  const handleDownloadCompleteReport = useCallback(
    async (questionnaire: { id: string }) => {
      try {
        await api.downloadQuestionnaireCompleteReport(questionnaire.id)
        snackActions.open('Your download should start soon!')
      } catch {
        snackActions.open('Ops! Something went wrong trying to download the report, please try again later.')
      }
    },
    [snackActions]
  )

  const handleStats = useCallback(
    async (questionnaire: { id: string; title: string }) => {
      await api.getQuestionnaireStats(questionnaire.id).then(response => {
        simpleDialogActions.open(
          'Statistics for: ' + questionnaire.title,
          <>
            <Box>
              <Typography fontWeight="700" fontSize={'1em'}>
                Invitations sent: {response.invites.length}
              </Typography>
              <Typography fontWeight="700" fontSize={'1em'}>
                Interviews not yet started: {response.notStarted}
              </Typography>
              <Typography fontWeight="700" fontSize={'1em'}>
                Interviews in process: {response.inProcess}
              </Typography>
              <Typography fontWeight="700" fontSize={'1em'}>
                Interviews completed: {response.completed}
              </Typography>
              <Typography fontWeight="700" fontSize={'1em'}>
                Skipped questions: {response.skipped}
              </Typography>
              <Typography fontWeight="700" fontSize={'1em'}>
                Completion percentage: {response.percentage}%
              </Typography>
            </Box>
            <br />
            <Box>
              <Button
                color="primary"
                onClick={() => {
                  handleDownloadStats(questionnaire)
                }}
              >
                Download Statistics (CSV)
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  handleDownloadCompleteReport(questionnaire)
                }}
              >
                Download Submission Details
              </Button>
            </Box>
          </>,
          {
            cancelText: 'Close',
            showOk: false
          }
        )
      })
    },
    [simpleDialogActions, handleDownloadStats, handleDownloadCompleteReport]
  )

  const handleOpenReviews = useCallback(
    (reviewers: QuestionnaireReviewer[]) => {
      const content: ReactNode = (
        <Box sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {reviewers.map((reviewer, idx) => {
            return (
              <>
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>{reviewer.reviewerName}</Typography>
                  {reviewer.isReviewed ? (
                    reviewer.isApproved ? (
                      <CheckIcon color={'primary'} />
                    ) : (
                      <DoDisturbIcon sx={{ color: 'red' }} />
                    )
                  ) : (
                    <AccessTimeIcon />
                  )}
                </Box>
                {idx !== reviewers.length - 1 && <Box sx={{ height: '1px', backgroundColor: 'gray' }} />}
              </>
            )
          })}
        </Box>
      )
      simpleDialogActions.open('Reviewers', content, { cancelText: 'Close' })
    },
    [simpleDialogActions]
  )

  const handleSeeReviews = useCallback(
    async (quiz: QuizInviteType) => {
      if (!quiz.reviewers || quiz.reviewers.length === 0) {
        snackActions.open('Oops! It seems there are no team members reviewing this questionnaire.')
        return
      }
      handleOpenReviews(quiz.reviewers)
    },
    [snackActions, handleOpenReviews]
  )

  const options = useMemo(() => [{ decision: 'approve' }, { decision: 'disapprove' }], [])

  const handleReview = useCallback(
    (quetionnaireId: string) => {
      formDialogActions.open(
        'Review Questionnaire',
        '',
        [
          {
            label: 'Decision',
            name: 'decision',
            placeholder: 'Decision',
            type: 'select',
            options: options.map(opt => (
              <MenuItem key={opt.decision} value={opt.decision}>
                {capitalize(opt.decision)}
              </MenuItem>
            ))
          },
          {
            label: 'Reason',
            name: 'reason',
            placeholder: 'Reason',
            rows: 4,
            type: 'text'
          }
        ],
        { title: '', description: '', language: null },
        null,
        async (data: { decision: string; reason: string }) => {
          if (!data.decision) {
            snackActions.open('Please, select the decision as approve or disapprove.')
            return
          }
          const decision = data.decision === 'approve'
          try {
            await api.doReview({ questionnaireId: quetionnaireId, approved: decision, reason: data.reason })
            snackActions.open('Questionnaire reviewed successfully!')
            reload()
          } catch (error) {
            if (axios.isAxiosError(error)) {
              // @ts-ignore
              const { code, message } = error.response.data
              if (code) {
                snackActions.open(message)
                return
              }
            }
            snackActions.open('Internal server error.')
          }
        },
        { okText: 'Submit', cancelText: `Cancel` }
      )
    },
    [formDialogActions, snackActions, options, reload]
  )

  const handleInviteReviewer = useCallback(
    (quiz: QuizInviteType) => {
      const filteredAndTransformedMembers = filterAndTransformMembersByQuestionnaire(quiz.reviewers)
      if (!filteredAndTransformedMembers || filteredAndTransformedMembers.length === 0) {
        snackActions.open('Oops! It seems there are no team members you can invite right now.')
        return
      }
      simpleMultiSelectDialogActions.open(
        async (ids: string[] | null) => {
          if (!ids || ids.length === 0) {
            return
          }
          const tenantId = member?.tenant.id ? member?.tenant.id : user.tenant ? user.id : undefined
          const memberId = member?.id ?? undefined

          if (!tenantId) {
            return
          }

          try {
            await api.inviteQuestionnaireReviewers({
              questionnaireId: quiz.id,
              questionnaireReviewerIds: ids,
              tenantId: tenantId,
              memberId: memberId
            })
            reload()
            snackActions.open(`Team member${ids.length > 1 ? 's' : ''} invited successfully!`)
          } catch {
            snackActions.open('Ops! Something went wrong inviting your team members, please try again.')
          }
        },
        'Invite Reviewers',
        `Below is a list of your available team members whom you can invite.`,
        filteredAndTransformedMembers,
        'Invite'
      )
    },
    [filterAndTransformMembersByQuestionnaire, simpleMultiSelectDialogActions, snackActions, member, user, reload]
  )

  const handlePublished = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, quiz: QuizInviteType) => {
      try {
        const newPublishedValue = event.target.checked
        await api.updateQuestionnaire({ ...quiz, published: newPublishedValue, questionnaireId: quiz.id })
        setItems(prev => prev.map(item => (item.id === quiz.id ? { ...item, published: newPublishedValue } : item)))
      } catch (error) {
        // @ts-ignore
        const { data } = error.response

        if (data.code) {
          snackActions.open(data.message)
          return
        }
        snackActions.open(`Problem to make this ${event.target.checked ? 'draft' : 'published'}`)
      }
    },
    [snackActions, setItems]
  )

  const handleTogglePublic = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, quiz: QuizInviteType) => {
      try {
        const newPublicValue = event.target.checked
        await api.updateQuestionnaire({ ...quiz, privateQuestionnaire: newPublicValue, questionnaireId: quiz.id })
        setItems(prev =>
          prev.map(item =>
            item.id === quiz.id
              ? {
                  ...item,
                  privateQuestionnaire: newPublicValue
                }
              : item
          )
        )
      } catch (error) {
        // @ts-ignore
        const { data } = error.response

        if (data.code) {
          snackActions.open(data.message)
          return
        }
        snackActions.open(`Problem to make this ${event.target.checked ? 'private' : 'public'}`)
      }
    },
    [snackActions, setItems]
  )

  const createNewQuestionnaire = useCallback(() => {
    createQuestionnaireDialogActions.open(questionnaire => {
      if (!questionnaire) return
      if (Array.isArray(questionnaire)) {
        setItems(prev => [...questionnaire, ...prev])
      } else {
        setItems(prev => [questionnaire, ...prev])
      }
    })
  }, [createQuestionnaireDialogActions, setItems])

  useEffect(() => {
    let cancelled = false
    const getTeamMembers = async () => {
      try {
        const { content } = await api.getTeamMembers()
        if (!cancelled) setTeamMembers(content)
      } catch (e) {
        console.log(e)
      }
    }
    getTeamMembers()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (member?.role && member.role === TeamMemberRoleEnum.VIEWER) {
      history.push('a/my-library')
    }
  }, [member, history])

  const uniqueFilteredItems = useMemo(() => {
    const seen = new Set<string>()
    return items.filter(item => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      if (
        member?.role === TeamMemberRoleEnum.EDITOR &&
        !member.permissions.map(permission => permission.questionnaireId).includes(item.id)
      ) {
        return false
      }
      return true
    })
  }, [items, member])

  const handleShareClick = useCallback(
    async (questionnaire: QuizInviteType) => {
      const questionnaireId = questionnaire.id
      const invite = questionnaire.questions?.[0]?.collectionInvite
      if (invite) {
        handleShare(invite, undefined, undefined, undefined, true, true, questionnaireId)
        return
      }
      try {
        const shareInfo = await api.getQuestionnaireShareInfo(questionnaireId)
        const minimalInvite = {
          id: shareInfo.inviteId,
          url: shareInfo.url,
          questionnaireId
        }
        handleShare(minimalInvite as any, undefined, undefined, undefined, true, true, questionnaireId)
      } catch {
        snackActions.open('Unable to load share link. Please try again.')
      }
    },
    [handleShare, snackActions]
  )

  const handleStatsClick = useCallback(
    (questionnaire: QuizInviteType) => {
      handleStats(questionnaire)
    },
    [handleStats]
  )

  const handleConfigureClick = useCallback(
    (questionnaireId: string) => {
      history.push(`/a/questionnaire/${questionnaireId}`)
    },
    [history]
  )

  return (
    <Box component="form" sx={{ width: '100%', padding: 2 }}>
      <Box sx={{ paddingY: 2 }}>
        <Typography variant="h6">Questionnaire</Typography>
      </Box>
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '8px',
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <TextField
              key="search"
              name="search"
              sx={{
                minWidth: { xs: '300px', md: '422px' }
              }}
              rows={4}
              autoComplete="off"
              placeholder="Type questionnaire title or description to filter"
              margin="dense"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && e.preventDefault()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Button variant="outlined" sx={{ height: '56px' }} onClick={createNewQuestionnaire} disabled={!isCreator}>
              New questionnaire
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            {uniqueFilteredItems.map(questionnaire => {
              return (
                <QuizItem key={questionnaire.id} quiz={questionnaire}>
                  {isCreator && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={questionnaire.published ?? questionnaire.isPublished ?? false}
                          onChange={event => handlePublished(event, questionnaire)}
                          value={questionnaire.published ?? questionnaire.isPublished ?? false}
                        />
                      }
                      label={
                        questionnaire.published ?? questionnaire.isPublished ? (
                          <Typography sx={{ color: '#BDBDBD', fontSize: '14px' }}>Published</Typography>
                        ) : (
                          <Typography sx={{ color: '#BDBDBD', fontSize: '14px' }}>Draft</Typography>
                        )
                      }
                    />
                  )}
                  {isCreator && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={questionnaire.privateQuestionnaire}
                          onChange={event => handleTogglePublic(event, questionnaire)}
                          value={questionnaire.privateQuestionnaire}
                        />
                      }
                      label={
                        questionnaire.privateQuestionnaire ? (
                          <Typography sx={{ color: '#BDBDBD', fontSize: '14px' }}>Private</Typography>
                        ) : (
                          <Typography sx={{ color: '#BDBDBD', fontSize: '14px' }}>Public</Typography>
                        )
                      }
                    />
                  )}
                  {(questionnaire.published ?? questionnaire.isPublished) && (
                    <Button color="secondary" onClick={() => handleShareClick(questionnaire)}>
                      Share
                    </Button>
                  )}
                  {(questionnaire.published ?? questionnaire.isPublished) && (
                    <Button color="secondary" onClick={() => handleStatsClick(questionnaire)}>
                      Statistics
                    </Button>
                  )}
                  {(questionnaire.published ?? questionnaire.isPublished) && (
                    <Button
                      color="secondary"
                      onClick={() => setReportQuestionnaire({ id: questionnaire.id, title: questionnaire.title })}
                    >
                      Report
                    </Button>
                  )}
                  {isCreator && (user?.userOfTenant || user?.tenant) && (
                    <Button color="secondary" onClick={() => handleInviteReviewer(questionnaire)}>
                      Invite Reviewers
                    </Button>
                  )}
                  {isCreator && (user?.userOfTenant || user?.tenant) && (
                    <Button color="secondary" onClick={() => handleSeeReviews(questionnaire)}>
                      Reviews
                    </Button>
                  )}
                  <Button color="secondary" onClick={() => handleConfigureClick(questionnaire.id)}>
                    Configure
                  </Button>

                  {isCreator && (
                    <Button color="error" onClick={() => handleDelete(questionnaire.id)}>
                      Delete
                    </Button>
                  )}
                </QuizItem>
              )
            })}
            {hasNextPage && (
              <Grid item xs>
                <div ref={sentryRef} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <InviteReportModal questionnaire={reportQuestionnaire} onClose={() => setReportQuestionnaire(null)} />
    </Box>
  )
}

export default QuestionnaireComponent
